/*
 * Endless entertainment gallery controller — MFL 1:1 port (2026-09-17).
 * Source: modules/endless-entertainment/apple-gallery.js (v=hover-video-v1).
 * Scoped per section root (.cs-ee) instead of document; otherwise unchanged.
 */
(() => {
  'use strict';

  function initEndlessGallery(section) {
  if (!section || section.__csEeInit) return;
  section.__csEeInit = true;

  const CYCLE_MS = 5000;
  const MOTION_MS = 800;
  const EASING = 'cubic-bezier(.32,.72,0,1)';

  const primaryGallery = section?.querySelector('#tv-media-gallery');
  const secondaryGallery = section?.querySelector('#fam-media-gallery');
  const primaryTrack = primaryGallery?.querySelector(':scope > .media-gallery-item-container');
  const secondaryTrack = secondaryGallery?.querySelector(':scope > .media-gallery-item-container');

  if (!section || !primaryGallery || !primaryTrack) {
    console.warn('[Clean Gallery] required DOM not found');
    return;
  }

  const primaryItems = Array.from(primaryTrack.children).filter((el) =>
    el.classList.contains('media-gallery-item')
  );
  const secondaryItems = secondaryTrack
    ? Array.from(secondaryTrack.children).filter((el) =>
        el.classList.contains('media-gallery-item')
      )
    : [];

  const count = primaryItems.length;
  const secondaryCount = secondaryItems.length;

  const allDotItems = Array.from(section.querySelectorAll('.media-gallery-dotnav-item'));
  allDotItems.slice(count).forEach((el) => el.remove());
  const dotItems = allDotItems.slice(0, count);
  const dotLinks = dotItems.map((li) => li.querySelector('.media-gallery-dotnav-link'));
  const pauseButton = section.querySelector('.media-gallery-dotnav-iconcontrol');

  if (count < 2) {
    console.warn('[Clean Gallery] not enough primary items');
    return;
  }

  /* With only seven custom slides there is no need for Apple's 9-dot truncation. */
  dotItems.forEach((li) => {
    [...li.classList]
      .filter((name) => name.startsWith('dotnav-trunc-'))
      .forEach((name) => li.classList.remove(name));
  });

  /* Custom cards are visual only: never navigate to captured Apple URLs. */
  section.querySelectorAll('#tv-media-gallery a, #fam-media-gallery a').forEach((a) => {
    a.removeAttribute('href');
    a.addEventListener('click', (event) => event.preventDefault());
  });

  let index = primaryItems.findIndex((el) =>
    el.classList.contains('current') || el.classList.contains('current-item')
  );
  if (index < 0) index = 0;

  let counter = index;
  let primaryStep = 0;
  let secondaryStep = 0;
  const primaryPrevSlots = 1;
  let secondaryPrevSlots = 3;

  let playing = true;
  let timer = null;
  let deadline = 0;
  let remaining = CYCLE_MS;
  let locked = false;

  let dragging = false;
  let dragStartX = 0;
  let dragDeltaX = 0;
  let suppressClick = false;

  let hoverVideoActive = false;
  let activeVideoPreview = null;
  let previewFrame = null;
  const videoPreviews = [];

  function measure() {
    const p = primaryItems[0].getBoundingClientRect();
    primaryStep = p.width || 1263;

    if (secondaryItems.length) {
      const s = secondaryItems[0].getBoundingClientRect();
      secondaryStep = s.width || 483;
      secondaryPrevSlots = Math.max(
        1,
        Math.min(
          secondaryCount - 1,
          Math.ceil((window.innerWidth / 2) / secondaryStep) + 1
        )
      );
    }
  }

  function slotFor(itemIndex, logicalCounter, itemCount, prevSlots) {
    const low = logicalCounter - prevSlots;
    return itemIndex + Math.ceil((low - itemIndex) / itemCount) * itemCount;
  }

  function setItemSlot(el, slot, step) {
    el.style.setProperty('transition', 'none', 'important');
    el.style.setProperty(
      'transform',
      `translate3d(${slot * step}px,0,0)`,
      'important'
    );
  }

  function prepareWorld(logicalCounter) {
    for (let i = 0; i < count; i += 1) {
      setItemSlot(
        primaryItems[i],
        slotFor(i, logicalCounter, count, primaryPrevSlots),
        primaryStep
      );
    }

    if (secondaryTrack && secondaryCount) {
      for (let i = 0; i < secondaryCount; i += 1) {
        setItemSlot(
          secondaryItems[i],
          slotFor(i, logicalCounter, secondaryCount, secondaryPrevSlots),
          secondaryStep
        );
      }
    }
  }

  function setTrack(track, x, animate) {
    if (!track) return;
    track.style.setProperty(
      'transition',
      animate ? `transform ${MOTION_MS}ms ${EASING}` : 'none',
      'important'
    );
    track.style.setProperty(
      'transform',
      `translate3d(${x}px,0,0)`,
      'important'
    );
  }

  function render(animate = false, dragX = 0) {
    setTrack(primaryTrack, -counter * primaryStep + dragX, animate);

    if (secondaryTrack && secondaryCount) {
      const ratio = primaryStep ? secondaryStep / primaryStep : 0;
      setTrack(
        secondaryTrack,
        -counter * secondaryStep + dragX * ratio,
        animate
      );
    }
  }

  function normalized(value, n) {
    return ((value % n) + n) % n;
  }

  // 触屏/小屏无 hover：当前视频卡自动播放（hover→touch 降级，移动端黑块修复）
  const touchLike = window.matchMedia('(hover: none), (max-width: 640px)');

  function syncVideoPlayback() {
    primaryItems.forEach((item, i) => {
      item.querySelectorAll('video').forEach((video) => {
        const keepPlaying = touchLike.matches
          ? i === index && !document.hidden
          : hoverVideoActive &&
            activeVideoPreview?.item === item &&
            i === index &&
            !document.hidden;

        if (!keepPlaying) {
          video.pause();
        } else if (video.paused) {
          video.loop = true;
          video.muted = true;
          video.play().catch(() => {});
        }
      });
    });
  }

  function updateState() {
    primaryItems.forEach((item, i) => {
      const active = i === index;
      item.classList.toggle('current', active);
      item.classList.toggle('current-item', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    if (secondaryCount) {
      const secondaryIndex = normalized(counter, secondaryCount);
      secondaryItems.forEach((item, i) => {
        const active = i === secondaryIndex;
        item.classList.toggle('current', active);
        item.classList.toggle('current-item', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    }

    dotItems.forEach((li, i) => {
      const active = i === index;
      li.classList.remove(
        'current',
        'dotnav-focused',
        'dotnav-incoming',
        'dotnav-outgoing',
        'dotnav-previous-current'
      );
      if (active) li.classList.add('current', 'dotnav-focused', 'dotnav-incoming');

      const link = dotLinks[i];
      if (link) {
        link.setAttribute('aria-selected', active ? 'true' : 'false');
        link.tabIndex = active ? 0 : -1;
        if (active) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      }
    });

    syncVideoPlayback();
  }


  function formatCountdown(seconds) {
    const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
    const whole = Math.ceil(safe);
    const minutes = Math.floor(whole / 60);
    const secs = String(whole % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  }

  function updateSoundButton(preview) {
    const soundOn = !preview.video.muted;
    preview.soundButton.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
    preview.soundButton.setAttribute(
      'aria-label',
      soundOn ? 'Mute preview audio' : 'Unmute preview audio'
    );
  }

  function updatePreviewCountdown(preview) {
    const duration = Number.isFinite(preview.video.duration)
      ? preview.video.duration
      : 0;
    const left = Math.max(0, duration - preview.video.currentTime);
    preview.countdown.textContent = formatCountdown(left);
  }

  function stopPreviewFrame() {
    if (previewFrame !== null) {
      cancelAnimationFrame(previewFrame);
      previewFrame = null;
    }
  }

  function runPreviewClock(preview) {
    stopPreviewFrame();

    const tick = () => {
      if (!hoverVideoActive || activeVideoPreview !== preview) {
        previewFrame = null;
        return;
      }
      updatePreviewCountdown(preview);
      previewFrame = requestAnimationFrame(tick);
    };

    tick();
  }

  /*
   * No artificial poster image is used.  We keep the real MP4 paused on its
   * first decoded frame, so the idle appearance is literally the video itself.
   */
  function showVideoFirstFrame(video) {
    video.pause();
    video.muted = true;

    const seek = () => {
      if (video.readyState < 2) return;
      const firstFrame = Number.isFinite(video.duration) && video.duration > 0
        ? Math.min(0.001, video.duration / 1000)
        : 0.001;
      try {
        if (Math.abs(video.currentTime - firstFrame) > 0.01) {
          video.currentTime = firstFrame;
        }
      } catch {}
    };

    if (video.readyState >= 2) seek();
    else video.addEventListener('loadeddata', seek, { once: true });
  }

  function stopVideoPreview({ resumeCarousel = true, resetVideo = true } = {}) {
    if (!activeVideoPreview) return;

    const preview = activeVideoPreview;
    stopPreviewFrame();

    preview.video.pause();
    preview.video.muted = true;
    updateSoundButton(preview);

    if (resetVideo) {
      showVideoFirstFrame(preview.video);
    }

    preview.item.classList.remove('is-video-previewing');
    hoverVideoActive = false;
    activeVideoPreview = null;
    section.classList.remove('local-gallery-hover-paused');
    updatePreviewCountdown(preview);
    updatePauseUI();

    if (resumeCarousel && playing && !document.hidden) {
      schedule(remaining > 0 ? remaining : CYCLE_MS);
    }
  }

  function startVideoPreview(preview) {
    const itemIndex = primaryItems.indexOf(preview.item);
    if (itemIndex !== index || document.hidden || dragging) return;

    if (activeVideoPreview === preview && hoverVideoActive) return;
    if (activeVideoPreview) {
      stopVideoPreview({ resumeCarousel: false, resetVideo: true });
    }

    if (playing && timer) {
      remaining = Math.max(0, deadline - performance.now());
    }
    clearTimer();

    hoverVideoActive = true;
    activeVideoPreview = preview;
    section.classList.add('local-gallery-hover-paused');
    preview.item.classList.add('is-video-previewing');

    /* Hover previews always begin muted. */
    preview.video.muted = true;
    updateSoundButton(preview);

    if (
      preview.video.ended ||
      (Number.isFinite(preview.video.duration) &&
        preview.video.currentTime >= preview.video.duration - 0.05)
    ) {
      try { preview.video.currentTime = 0; } catch {}
    }

    updatePauseUI();
    updatePreviewCountdown(preview);
    runPreviewClock(preview);

    const playResult = preview.video.play();
    if (playResult?.catch) {
      playResult.catch(() => {
        /* Muted hover playback should normally be allowed. */
      });
    }
  }

  function setupVideoPreviews() {
    primaryItems.forEach((item) => {
      const video = item.querySelector('.custom-gallery-video');
      if (!video) return;

      item.classList.add('custom-video-slide');
      video.autoplay = false;
      video.loop = false;
      video.muted = true;
      video.controls = false;
      video.preload = 'auto';
      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      video.removeAttribute('poster');
      video.setAttribute('preload', 'auto');

      const mediaHost = item.querySelector('.media-gallery-wrapper-link');
      if (!mediaHost) return;

      const ui = document.createElement('div');
      ui.className = 'custom-video-ui';
      ui.setAttribute('aria-hidden', 'false');
      ui.innerHTML = `
        <span class="custom-video-play-indicator" aria-hidden="true">
          <svg viewBox="0 0 32 32" aria-hidden="true">
            <path d="M11 7.6v16.8L24.5 16 11 7.6z"></path>
          </svg>
        </span>
        <button class="custom-video-sound custom-video-control" type="button"
          aria-label="Unmute preview audio" aria-pressed="false">
          <svg class="icon-sound-off" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M4 12h5l7-6v20l-7-6H4z"></path>
            <path d="M22.1 11.1 28 17m0-5.9L22.1 17" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></path>
          </svg>
          <svg class="icon-sound-on" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M4 12h5l7-6v20l-7-6H4z"></path>
            <path d="M20.5 11.5c2 2.1 2 6.9 0 9m3-12.3c4 4 4 11.6 0 15.6" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round"></path>
          </svg>
        </button>
        <span class="custom-video-countdown" aria-live="off">0:00</span>
      `;
      mediaHost.appendChild(ui);

      const soundButton = ui.querySelector('.custom-video-sound');
      const countdown = ui.querySelector('.custom-video-countdown');
      const preview = { item, video, ui, soundButton, countdown, mediaHost };
      videoPreviews.push(preview);

      const refreshDuration = () => updatePreviewCountdown(preview);
      video.addEventListener('loadedmetadata', refreshDuration);
      video.addEventListener('durationchange', refreshDuration);
      video.addEventListener('loadeddata', () => showVideoFirstFrame(video), { once: true });
      video.addEventListener('ended', () => {
        updatePreviewCountdown(preview);
        stopPreviewFrame();
        /* Keep the carousel frozen until the pointer actually leaves. */
      });

      soundButton.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      });

      soundButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!hoverVideoActive || activeVideoPreview !== preview) return;
        video.muted = !video.muted;
        updateSoundButton(preview);
      });

      item.addEventListener('pointerenter', (event) => {
        if (
          event.pointerType &&
          event.pointerType !== 'mouse' &&
          event.pointerType !== 'pen'
        ) return;
        startVideoPreview(preview);
      });

      item.addEventListener('pointerleave', () => {
        if (activeVideoPreview === preview) {
          stopVideoPreview({ resumeCarousel: true, resetVideo: true });
        }
      });

      updateSoundButton(preview);
      updatePreviewCountdown(preview);
      showVideoFirstFrame(video);
    });
  }

  function clearTimer() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function schedule(delay = CYCLE_MS) {
    clearTimer();
    if (!playing || hoverVideoActive || document.hidden) return;
    remaining = Math.max(0, delay);
    deadline = performance.now() + remaining;
    timer = setTimeout(() => moveBy(1, 'auto'), remaining);
  }

  function normalizeCounterIfNeeded() {
    const period = count * Math.max(1, secondaryCount || 1);
    if (Math.abs(counter) < period * 20) return;
    const turns = Math.trunc(counter / period);
    counter -= turns * period;
    prepareWorld(counter);
    render(false);
  }

  function moveBy(delta, source = 'control') {
    if (locked || !delta) return;

    if (hoverVideoActive) {
      stopVideoPreview({ resumeCarousel: false, resetVideo: true });
    }

    clearTimer();
    locked = true;

    const targetCounter = counter + delta;
    const targetIndex = normalized(targetCounter, count);

    prepareWorld(targetCounter);
    counter = targetCounter;
    index = targetIndex;
    updateState();
    render(true);

    window.setTimeout(() => {
      locked = false;
      normalizeCounterIfNeeded();
    }, MOTION_MS + 40);

    if (playing) schedule(CYCLE_MS);
  }

  function goTo(targetIndex) {
    if (locked || targetIndex === index) {
      if (playing) schedule(CYCLE_MS);
      return;
    }

    let delta = targetIndex - index;
    const half = count / 2;
    if (delta > half) delta -= count;
    if (delta < -half) delta += count;
    moveBy(delta, 'dot');
  }

  dotLinks.forEach((link, i) => {
    if (!link) return;
    link.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      goTo(i);
    });
  });

  function updatePauseUI() {
    section.classList.toggle('local-gallery-paused', !playing);
    if (pauseButton) {
      pauseButton.setAttribute(
        'aria-label',
        playing
          ? 'Pause endless entertainment gallery'
          : 'Play endless entertainment gallery'
      );
    }
    syncVideoPlayback();
  }

  if (pauseButton) {
    pauseButton.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (playing) {
          if (!hoverVideoActive) {
            remaining = Math.max(0, deadline - performance.now());
          }
          playing = false;
          clearTimer();
        } else {
          playing = true;
          if (!hoverVideoActive) schedule(remaining || CYCLE_MS);
        }
        updatePauseUI();
      },
      true
    );
  }

  primaryGallery.addEventListener('pointerdown', (event) => {
    if (event.target.closest?.('.custom-video-control')) return;
    if (locked || (event.button !== undefined && event.button !== 0)) return;
    if (hoverVideoActive) {
      stopVideoPreview({ resumeCarousel: false, resetVideo: true });
    }
    dragging = true;
    dragStartX = event.clientX;
    dragDeltaX = 0;
    suppressClick = false;
    clearTimer();
    primaryGallery.classList.add('is-local-dragging');
    try { primaryGallery.setPointerCapture(event.pointerId); } catch {}
  });

  primaryGallery.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    dragDeltaX = event.clientX - dragStartX;
    if (Math.abs(dragDeltaX) > 8) suppressClick = true;
    render(false, dragDeltaX);
  });

  function finishDrag(event) {
    if (!dragging) return;
    dragging = false;
    primaryGallery.classList.remove('is-local-dragging');

    const threshold = primaryStep * 0.12;
    const delta = dragDeltaX;
    dragDeltaX = 0;

    if (delta < -threshold) moveBy(1, 'drag');
    else if (delta > threshold) moveBy(-1, 'drag');
    else {
      render(true);
      if (playing) schedule(CYCLE_MS);
    }

    try { primaryGallery.releasePointerCapture(event.pointerId); } catch {}
  }

  primaryGallery.addEventListener('pointerup', finishDrag);
  primaryGallery.addEventListener('pointercancel', finishDrag);
  primaryGallery.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    },
    true
  );

  primaryGallery.tabIndex = 0;
  primaryGallery.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveBy(1, 'keyboard');
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveBy(-1, 'keyboard');
    } else if (event.key === ' ') {
      event.preventDefault();
      pauseButton?.click();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (hoverVideoActive) {
        stopVideoPreview({ resumeCarousel: false, resetVideo: true });
      }
      clearTimer();
      syncVideoPlayback();
    } else if (playing) {
      schedule(remaining || CYCLE_MS);
      syncVideoPlayback();
    }
  });

  let resizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      measure();
      prepareWorld(counter);
      render(false);
    }, 120);
  });

  if (location.hash.startsWith('#endless-entertainment-gallery-item-')) {
    history.replaceState(null, '', location.pathname + location.search);
  }

  setupVideoPreviews();
  measure();
  prepareWorld(counter);
  updateState();
  updatePauseUI();
  render(false);

  requestAnimationFrame(() => {
    const active = dotItems[index];
    if (active) {
      active.classList.remove('current', 'dotnav-incoming');
      void active.offsetWidth;
      active.classList.add('current', 'dotnav-focused', 'dotnav-incoming');
    }
    schedule(CYCLE_MS);
  });

  console.log('[CS Endless] ready', {
    primaryItems: count,
    secondaryItems: secondaryCount,
    primaryStep: Math.round(primaryStep),
    secondaryStep: Math.round(secondaryStep),
    cycle: CYCLE_MS
  });
  }

  function boot() {
    document.querySelectorAll('.cs-ee[data-cs-ee]').forEach(initEndlessGallery);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
